import { useCallback, useEffect, useRef, useState } from 'react';
import { useFlag } from 'shared/settings/FeatureToggles';

export type AnimationTargetTab =
  | 'new'
  | 'in_terminal'
  | 'assigned'
  | 'picked_up'
  | 'delivered';

export function getAnimationTargetTab(
  action: string,
): AnimationTargetTab | null {
  switch (action) {
    case 'assign':
      return 'assigned';
    case 'mark_as_picked_up':
      return 'picked_up';
    case 'mark_as_delivered':
      return 'delivered';
    case 'mark_as_new':
    case 'unassign':
      return 'new';
    case 'mark_as_in_terminal':
      return 'in_terminal';
    default:
      return null;
  }
}

export function useLoadAnimation() {
  const [isAnimating, setIsAnimating] = useState(false);
  const shouldShowLoadAnimation = useFlag('load_animation');
  const [targetTab, setTargetTab] = useState<AnimationTargetTab>('assigned');
  const [animationStartPosition, setAnimationStartPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const animatedImageRef = useRef<HTMLImageElement>(null);
  const loadCardRefs = useRef<Map<string, HTMLElement>>(new Map());

  const triggerAnimation = useCallback(
    (loadGUID: string, tab: AnimationTargetTab) => {
      const loadCardElement = loadCardRefs.current.get(loadGUID);
      if (loadCardElement && shouldShowLoadAnimation) {
        const rect = loadCardElement.getBoundingClientRect();
        setAnimationStartPosition({
          x: rect.left + rect.width / 2.8,
          y: rect.top + rect.height / 2.5,
        });
        setTargetTab(tab);
        setIsAnimating(true);
      }
    },
    [shouldShowLoadAnimation],
  );

  useEffect(() => {
    let animation: Animation | undefined;

    if (
      isAnimating &&
      animatedImageRef.current &&
      animationStartPosition &&
      shouldShowLoadAnimation
    ) {
      const tabElement = document.querySelector(
        `[data-intercom-target="loads-tab-${targetTab}"]`,
      );
      const targetRect = tabElement?.getBoundingClientRect();

      const imageWidth = 220;
      const imageHeight = 80;
      const targetX = targetRect
        ? targetRect.left + targetRect.width / 2 - imageWidth / 2
        : 300;
      const targetY = targetRect
        ? targetRect.top + targetRect.height / 2 - imageHeight / 2
        : 150;

      animation = animatedImageRef.current.animate(
        [
          {
            left: `${animationStartPosition.x}px`,
            top: `${animationStartPosition.y}px`,
            transform: 'scale(1)',
            opacity: 1,
          },
          {
            left: `${(animationStartPosition.x + targetX) / 2}px`,
            top: `${(animationStartPosition.y + targetY) / 2}px`,
            transform: 'scale(0.8)',
            opacity: 1,
            offset: 0.5,
          },
          {
            left: `${targetX}px`,
            top: `${targetY}px`,
            transform: 'scale(0.3)',
            opacity: 0,
          },
        ],
        {
          duration: 1100,
          easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        setIsAnimating(false);
        setAnimationStartPosition(null);
      };
    }

    return () => {
      animation?.cancel();
    };
  }, [isAnimating, animationStartPosition, targetTab, shouldShowLoadAnimation]);

  return {
    isAnimating,
    loadCardRefs,
    triggerAnimation,
    animatedImageRef,
    animationStartPosition,
  };
}
